import { FormattedMessage } from 'react-intl';
import { OptionProps } from 'react-select';

import {
    DEBIT_CARD_TYPE,
    MANUAL_BANK_TRANSFER_TYPE,
    TRUELAYER_REPAYMENT_TYPE
} from '../EarlyRepaymentHelper';
import { PaymentMethodLogo } from './PaymentMethodLogo';
import styles from './PaymentMethodOption.module.css';
import type { PaymentOption } from './SelectPaymentMethod';

export const PaymentMethodOption = (
    props: OptionProps<PaymentOption, false>
) => {
    const { data, innerProps, innerRef, isSelected } = props;

    const sharedProps = {
        ...innerProps,
        ref: innerRef,
        className: styles.PaymentMethodOption,
        role: 'option' as const,
        'aria-label': data.label,
        'aria-selected': isSelected
    };

    switch (data.type) {
        case MANUAL_BANK_TRANSFER_TYPE:
            return (
                <div {...sharedProps}>
                    <ManualBankTransferLabel />
                </div>
            );
        case DEBIT_CARD_TYPE:
            return (
                <div {...sharedProps}>
                    <CardLabel
                        cardLastDigits={data.cardLastDigits ?? ''}
                        issuer={data.issuer ?? ''}
                    />
                </div>
            );
        case 'BANK_ACCOUNT':
            return (
                <div {...sharedProps}>
                    <BankAccountLabel
                        label={data.label ?? ''}
                        issuer={data.issuer ?? ''}
                    />
                </div>
            );
        case TRUELAYER_REPAYMENT_TYPE:
            return (
                <div {...sharedProps}>
                    <TruelayerLabel />
                </div>
            );
        default:
            return null;
    }
};

const CardLabel = ({
    cardLastDigits,
    issuer
}: {
    cardLastDigits: string;
    issuer: string;
}) => (
    <div className={styles.labelContainer}>
        <PaymentMethodLogo
            type={DEBIT_CARD_TYPE}
            issuer={issuer}
            logoStyle={styles.icon}
        />
        <FormattedMessage
            id="_.select_payment_method.card_option"
            defaultMessage="Card ●●●● {cardLastDigits}"
            values={{ cardLastDigits: cardLastDigits }}
        />
    </div>
);

const BankAccountLabel = ({
    label,
    issuer
}: {
    label: string;
    issuer: string;
}) => (
    <div className={styles.labelContainer}>
        <PaymentMethodLogo
            type={DEBIT_CARD_TYPE}
            issuer={issuer}
            logoStyle={styles.icon}
        />
        <FormattedMessage
            id="_.select_payment_method.bank_account_option"
            defaultMessage="Account {accountNumber}"
            values={{ accountNumber: label }}
        />
    </div>
);

const ManualBankTransferLabel = () => (
    <div className={styles.labelContainer}>
        <PaymentMethodLogo
            type={MANUAL_BANK_TRANSFER_TYPE}
            logoStyle={styles.icon}
        />
        <FormattedMessage
            id="_.select_payment_method.manual_payment"
            defaultMessage="Manual Bank Transfer"
        />
    </div>
);

const TruelayerLabel = () => (
    <div className={styles.labelContainer}>
        <PaymentMethodLogo
            type={TRUELAYER_REPAYMENT_TYPE}
            logoStyle={styles.icon}
        />
        <FormattedMessage
            id="_.select_payment_method.truelayer_repayment"
            defaultMessage="Instant Bank Transfer"
        />
        <span className={styles.recommendedTag}>Recommended</span>
    </div>
);
